import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgFor, NgIf, DatePipe, TitleCasePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../services/reports/reports.service';
import { MessageModalService } from '../../services/message-modal/message-modal.service';

interface JobGroup {
  status: string;
  jobs: any[];
  columns: string[];
}

interface DayCount {
  date: string; // e.g. "Mar 05"
  count: number;
  barHeight: number; // pixel height for the bar
}

interface BarChartData {
  status: string;
  totalCount: number;
  days: DayCount[];
  maxCount: number;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [NgFor, NgIf, MatTableModule, MatExpansionModule, MatProgressSpinnerModule, MatIconModule, MatCardModule],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css',
})
export class ReportsComponent implements OnInit, OnDestroy {
  readonly CHART_HEIGHT = 180; // total chart area height in pixels
  readonly BAR_MAX_HEIGHT = 160; // max bar height, leaving room for value labels
  runningGroup: JobGroup | null = null;
  succeededChart: BarChartData | null = null;
  failedChart: BarChartData | null = null;
  loading = true;
  totalJobCount = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private reportsService: ReportsService,
    private messageModal: MessageModalService
  ) {}

  ngOnInit(): void {
    this.loadJobs();
  }

  loadJobs(): void {
    this.loading = true;
    this.reportsService
      .getJobs()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          const jobs: any[] = response?.jobs ?? [];

          // Filter to jobs less than 30 days old based on start_time
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

          const recentJobs = jobs.filter((job) => {
            const startTime = new Date(job.start_time);
            return startTime >= thirtyDaysAgo;
          });

          this.totalJobCount = recentJobs.length;

          // Group by status
          const grouped = new Map<string, any[]>();
          for (const job of recentJobs) {
            const status = job.status ?? 'unknown';
            if (!grouped.has(status)) {
              grouped.set(status, []);
            }
            grouped.get(status)!.push(job);
          }

          // Build running jobs table
          const runningJobs = grouped.get('running') ?? [];
          if (runningJobs.length > 0) {
            this.runningGroup = this.buildJobGroup('running', runningJobs);
          } else {
            this.runningGroup = null;
          }

          // Build bar charts for succeeded and failed
          const succeededJobs = grouped.get('succeeded') ?? [];
          this.succeededChart = this.buildBarChart('succeeded', succeededJobs);

          const failedJobs = grouped.get('failed') ?? [];
          this.failedChart = this.buildBarChart('failed', failedJobs);
        },
        error: (err) => {
          this.loading = false;
          console.error('Error fetching jobs:', err);
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching jobs data.`);
        },
      });
  }

  private buildJobGroup(status: string, jobs: any[]): JobGroup {
    const columnSet = new Set<string>();
    for (const job of jobs) {
      Object.keys(job).forEach((key) => columnSet.add(key));
    }
    const orderedKnownColumns = ['name', 'namespace', 'org_id', 'user_id', 'status', 'start_time', 'end_time', 'uid'];
    const orderedColumns = [
      ...orderedKnownColumns.filter((c) => columnSet.has(c)),
      ...[...columnSet].filter((c) => !orderedKnownColumns.includes(c)).sort(),
    ];
    return { status, jobs, columns: orderedColumns };
  }

  private buildBarChart(status: string, jobs: any[]): BarChartData {
    // Build a map of date -> count for the last 30 days
    const dayCounts = new Map<string, number>();

    // Initialize all 30 days
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = this.toDateKey(d);
      dayCounts.set(key, 0);
    }

    // Count jobs per day
    for (const job of jobs) {
      const startTime = new Date(job.start_time);
      const key = this.toDateKey(startTime);
      if (dayCounts.has(key)) {
        dayCounts.set(key, dayCounts.get(key)! + 1);
      }
    }

    const maxCount = Math.max(1, ...dayCounts.values());

    const days: DayCount[] = [];
    for (const [dateKey, count] of dayCounts) {
      days.push({
        date: this.formatDateLabel(dateKey),
        count,
        barHeight: maxCount > 0 ? Math.round((count / maxCount) * this.BAR_MAX_HEIGHT) : 0,
      });
    }

    return {
      status,
      totalCount: jobs.length,
      days,
      maxCount,
    };
  }

  private toDateKey(d: Date): string {
    // YYYY-MM-DD
    return d.toISOString().slice(0, 10);
  }

  private formatDateLabel(dateKey: string): string {
    const d = new Date(dateKey + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // --- Table helpers (kept for running jobs table) ---

  private columnLabels: Record<string, string> = {
    name: $localize`Job Name`,
    namespace: $localize`Namespace`,
    org_id: $localize`Org ID`,
    user_id: $localize`User ID`,
    status: $localize`Status`,
    start_time: $localize`Start Time`,
    end_time: $localize`End Time`,
    uid: $localize`UID`,
  };

  formatColumnLabel(column: string): string {
    return this.columnLabels[column] ?? column.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  formatCellValue(value: any, column: string): string {
    if (value == null || value === '') {
      return '—';
    }
    if (column.includes('time') || column.includes('date')) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(date);
      }
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'running':
        return 'play_circle';
      case 'succeeded':
      case 'complete':
      case 'completed':
        return 'check_circle';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'running':
        return 'status-running';
      case 'succeeded':
      case 'complete':
      case 'completed':
        return 'status-succeeded';
      case 'failed':
      case 'error':
        return 'status-failed';
      default:
        return 'status-default';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
